// 原系統主體區欄位的**規則層**（資料層是 legacy-fields.generated.ts，由
// scripts/extract-legacy-fields.ts 從 legacy raw DOM 產生，不手改）。
//
// 分工與 legacy-table-headers / page-columns 那一組相同：那邊是資料，這邊是規則。
//
// ---------------------------------------------------------------------------
// 這一層做四件事
// ---------------------------------------------------------------------------
// 1. **依 name 去重**。功能契約以 `name` 為比對 key（FUNCTIONAL-CONTRACT.md D-2，
//    scripts/compare-contracts.ts 的 `fieldKey`），React 側在抽取時也是以 name 建 Map、
//    先出現的贏。同一頁出現多次的 name 只畫一個控制項就夠了；選哪一個變體會影響
//    type/required/hidden 的比對，所以取**出現次數最多**的那個變體（多數決），
//    而不是「第一個」——第一個常常是隱藏的旗標欄。
//
// 2. **排除 CSRF**。`csrfToken`／`anti-CSRF`／`_csrf` 一律不畫：雛形沒有 session、
//    沒有 CSRF 機制，畫一個永遠空值、送出時也沒有人驗的 hidden input，就是
//    criteria C5-e 定義的假欄位。這些項在 criteria 的 C1-a 分母外，歸 (e) 並附固定 todo。
//
// 3. **提供 select 的可選項**。static 列舉直接用抽到的列舉代碼（那是欄位定義的一部分）。
//    dynamic（外鍵）下拉的選項在原系統是**資料庫內容**，功能契約明寫這種 options
//    「只比存在與 multiple」（FUNCTIONAL-CONTRACT.md:59，compare-contracts 的 optionsEqual
//    對 `kind === 'dynamic'` 直接回 null）——也就是說它的值**不參與任何比對**。
//    這裡改用雛形自己的假資料層（fixtures.buildValues）產生三個選項：
//      - 不搬原系統的資料值（TEST-DATA-AND-PII-POLICY §3.2）；
//      - 不影響任何契約數字（dynamic options 不比值）；
//      - 讓控制項真的可以被選、被送出，否則就是「畫出來但不能用」的空殼。
//    值是 fixtures 的確定性假資料，同一個 (pageId, name) 永遠得到同一組。
//
// 4. **判斷哪些控制項「沒有使用者事件可以改值」，並保證它們照樣進 payload**（見下一段）。
//
// ---------------------------------------------------------------------------
// FC-3 R2：不可驅動的控制項（C5-h 的真正難點）
// ---------------------------------------------------------------------------
// R1 交付被驗出 C5-h 只有 93.5%～94.1%。根因不是漏接線，是**有兩類控制項在 React 裡
// 根本不存在「使用者改值」這個事件**，而 R1 的 `toPayload()` 又只帶非空值：
//
//   ① `<input type="hidden">`：React 的合成事件系統不把 type=hidden 當文字輸入，
//      `onChange` 永遠不會觸發（驗證者用最小探針做過陽性對照：text 觸發、hidden 不觸發）。
//      全庫 42 個、分布 28 頁。
//   ② 沒有任何「可選出非空值」的 `<select>`：legacy 這幾個下拉的 `<option>` 是 JS 執行期
//      才填的，raw DOM 裡是 0 個 option（`account` ×3）或只有一個 `value=""` 的
//      提示項（`formTag`）。契約記 `options.kind='static'`、值集合就是那樣，
//      React 若硬塞選項會讓值集合不同而**判不相符** —— 所以不能為了數字塞選項。
//
// 這兩類的「接線」定義不可能是「使用者改值後送出」，只能是
// **「它的值一定會被送進 DataSource」**。所以：
//   - `seedValueFor()` 用 legacy raw DOM 的 `value` 屬性當初始值（見下面的取捨），
//   - `isUserDrivable()` 判斷這個控制項有沒有使用者事件，
//   - `toPayload()` 對**不可驅動**的欄位一律帶出 key（即使值是空字串），
//     對可驅動的欄位維持「空的就不帶」——後者是 C6 變異 2（拿掉 onChange）能紅的關鍵。
//
// ---------------------------------------------------------------------------
// hidden 初始值的取捨：帶「常數參數」，不帶「資料」
// ---------------------------------------------------------------------------
// 實測 42 個 hidden 的 legacy `value` 幾乎全是**與資料列無關的常數參數**：
//   refType=4…10、queryType=0/1、purpose=query、source=student/teacher、
//   isEraseMe=false、todo=false、departmentId0=-1、planId=0、formTemplateId=0、
//   action=""、fromPage=""、ids=""…
// 這些是「這一頁用哪個模式開啟」的參數，不是使用者資料，照抄它才是忠於 legacy。
//
// 但有兩種例外**不帶**（帶了就是把原系統的真實資料搬進雛形，違反
// TEST-DATA-AND-PII-POLICY §3.2）：
//   - **指向真實資料列的識別碼**：name 命中外鍵樣式且值不是哨兵值（''／'0'／'-1'）。
//     例：`profile-profile-maintenance` 的 `id="5"`（那是登入帳號的真實 id）。
//   - **擷取當下算出來的日期**：例 `leave-leave-holiday` 的 `begin="2026-08-30"`／
//     `end="2026-10-11"`，那是抓畫面那一天算出來的區間，不是常數。
// 這兩種一律 seed 成空字串，但**仍然進 payload**（key 一定送得出去）。

import { buildValues, type Column } from '@/mocks/fixtures';
import {
  LEGACY_FIELDS,
  type LegacyFieldControl,
  type LegacyFieldOption,
} from '@/mocks/legacy-fields.generated';
import { inferKind, matchKindRule } from '@/mocks/page-columns';

export type { LegacyFieldControl, LegacyFieldOption };

/**
 * 不畫的欄位：雛形沒有 CSRF 機制（criteria Q2 裁決 1 —— 不畫、不進 notCompared，
 * 歸 (e) 附固定 todo「待接後端 session」）。
 */
export const CSRF_NAME = /^(csrfToken|anti-CSRF|_csrf)$/i;

/**
 * 外鍵樣式的 name。逐字鏡像 `scripts/contract-extract-core.ts:92` 的 `FOREIGN_KEY_NAME`
 * （該檔是 C3 受保護檔、又是 script 不能被 src/ import，所以在此重新宣告一份；
 * 由 `src/mocks/legacy-fields.test.ts` 的「鏡像常數與抽取器逐字相同」測試把關）。
 */
export const FOREIGN_KEY_NAME = /id\d*$|^(annual|organization|formDepartment|formTemplate)\d*$/i;

/** 哨兵值：代表「未指定／新增」，不是任何一列真實資料。 */
export const SENTINEL_VALUES: ReadonlySet<string> = new Set(['', '0', '-1']);

/** 擷取當下算出來的日期，不是常數參數。 */
export const DATE_LIKE = /^\d{4}[-/]\d{1,2}[-/]\d{1,2}/;

/** 這一項因為沒有資料來源而刻意不畫（C5-h 的反向面，必須揭露）。 */
export function isSkipped(field: LegacyFieldControl): boolean {
  return CSRF_NAME.test(field.name);
}

/** 去重時用來分辨「同一個 name 的不同變體」的鍵。分隔字元用可見的 `¦`。 */
export function variantKeyOf(field: LegacyFieldControl): string {
  const options = (field.options ?? []).map((option) => option.value).join('¦');
  return [
    field.type,
    String(field.required),
    String(field.hidden),
    String(field.multiple),
    field.optionsKind ?? '',
    options,
  ].join('¦');
}

/**
 * 批次 43（#41）：label 逐字勘誤表。generated 抽取把 `<span class=required-star>*</span>\n學年度：`
 * （星號 span 與文字間的換行空格）攤平成 `'*學年度：'`（漏空格）；legacy raw 為 `'* 學年度：'`。
 * generated 為紅線不可手改，在規則層套用（鍵＝pageId¦name）。實測受影響＝study-record-fill/status
 * 四欄（annual/organization/jobType/department）。
 */
export const FIELD_LABEL_OVERRIDES: Readonly<Record<string, string>> = {
  'training-study-record-study-record-fill¦annualId': '* 學年度：',
  'training-study-record-study-record-fill¦organizationId': '* 醫院：',
  'training-study-record-study-record-fill¦jobTypeId': '* 訓練類別：',
  'training-study-record-study-record-fill¦departmentId': '* 部門：',
  'training-study-record-study-record-fill¦planTargetId': '* 訓練計畫：',
  'training-study-record-study-record-status¦annualId': '* 學年度：',
  'training-study-record-study-record-status¦organizationId': '* 醫院：',
  'training-study-record-study-record-status¦jobTypeId': '* 訓練類別：',
  'training-study-record-study-record-status¦departmentId': '* 部門：',
  'training-study-record-study-record-status¦planTargetId': '* 訓練計畫：',

  //   //  JSP 原始有空白者＝legacy raw-dom 文字帶空格，generated 失落；逐欄位 override 避免誤傷
  //  246 處本來就無空白的 JSP）。
  'announcement-announcement-list¦type': '* 公告類型：',
  'announcement-announcement-list¦refId': '* 公告單位：',
  'announcement-announcement-list¦object': '* 公告對象：',
  'announcement-announcement-list¦status': '* 公告狀態：',
  'announcement-announcement-list¦enableStatus': '* 有效狀態：',
  'discuss-discuss-type¦organizationId': '* 醫院：',
  'discuss-discuss-type¦jobTypeId': '* 訓練類別：',
  'exam-exam-manage¦organizationId': '* 醫院：',
  'exam-exam-manage¦jobTypeId': '* 訓練類別：',
  'exam-exam-manage¦departmentId': '* 部門：',
  'exam-question-bank¦organizationId': '* 醫院：',
  'exam-question-bank¦jobTypeId': '* 訓練類別：',
  'exam-question-bank¦departmentId': '* 部門：',
  'instruction-plan-target-plan-target-list¦annualId': '* 學年度：',
  'instruction-plan-target-plan-target-list¦organizationId': '* 醫院：',
  'instruction-study-item-maintain¦organizationId': '* 醫院：',
  'instruction-study-item-maintain¦jobTypeId': '* 訓練類別：',
  'instruction-study-item-maintain¦departmentId': '* 部門：',
  'instruction-study-item-maintain¦studyItemTypeId': '* 學習項目類別：',
  'instruction-study-item-type-maintain¦organizationId': '* 醫院：',
  'instruction-study-item-type-maintain¦jobTypeId': '* 訓練類別：',
  'instruction-study-item-type-maintain¦departmentId': '* 部門：',
  'leave-leave-annual¦annualId': '* 學年度：',
  'leave-leave-annual¦organizationId': '* 醫院：',
  'leave-leave-annual¦jobTypeId': '* 訓練類別：',
  'leave-leave-annual¦planTargetId': '* 部門：',
  'leave-leave-form¦annualId': '* 學年度：',
  'leave-leave-form¦organizationId': '* 醫院：',
  'leave-leave-form¦jobTypeId': '* 訓練類別：',
  'leave-leave-form¦planId': '* 訓練計畫：',
  'leave-leave-type¦organizationId': '* 醫院：',
  'leave-leave-type¦jobTypeId': '* 訓練類別：',
  'leave-leave-type¦isEnable': '* 啟用狀態：',
  'config-comment¦organizationId': '* 醫院：',
  'config-comment¦jobTypeId': '* 訓練類別：',
  'config-comment¦departmentId': '* 部門：',
  'config-comment¦type': '* 類別：',
  'config-comment¦isEnable': '* 狀態：',
  'system-api-log¦queryForm_startDate': '* 請求時間：',
  };

/**
 * 批次 44（#42）：逐頁排除的契約欄位（行為層 queryTailFields 自畫群組取代；
 * comment selePrivate＝契約抽到的 label 結構與 legacy 不同（label(常用語：)>input vs
 * label(常用語：)＋input＋label(只顯示自已的常用語內容)），由行為層整組鏡像）。
 */
export const EXCLUDED_QUERY_FIELDS: Readonly<Record<string, true>> = {
  'config-comment¦selePrivate': true,
};

const cache = new Map<string, readonly LegacyFieldControl[]>();

/**
 * 依 name 去重後、該頁要畫的欄位。順序沿用 raw DOM 的原始順序。
 * 該頁沒有任何可畫欄位時回傳 undefined。
 */
export function legacyContractFieldsFor(pageId: string): readonly LegacyFieldControl[] | undefined {
  const cached = cache.get(pageId);
  if (cached !== undefined) {
    return cached.length === 0 ? undefined : cached;
  }
  const list = LEGACY_FIELDS[pageId];
  if (list === undefined) {
    return undefined;
  }
  // 多數決：同一個 name 的多個變體裡，挑「同樣的 type/required/hidden/multiple/options」
  // 出現次數最多的那一個。平手時取先出現的（與 React observed 的 Map 語意一致）。
  const groups = new Map<string, LegacyFieldControl[]>();
  const order: string[] = [];
  for (const field of list) {
    if (isSkipped(field)) continue;
    const bucket = groups.get(field.name);
    if (bucket === undefined) {
      groups.set(field.name, [field]);
      order.push(field.name);
    } else {
      bucket.push(field);
    }
  }
  const picked: LegacyFieldControl[] = [];
  for (const name of order) {
    const bucket = groups.get(name) ?? [];
    const counts = new Map<string, { field: LegacyFieldControl; count: number }>();
    for (const field of bucket) {
      const key = variantKeyOf(field);
      const entry = counts.get(key);
      if (entry === undefined) counts.set(key, { field, count: 1 });
      else entry.count += 1;
    }
    let best: { field: LegacyFieldControl; count: number } | undefined;
    for (const entry of counts.values()) {
      if (best === undefined || entry.count > best.count) best = entry;
    }
  if (best !== undefined) picked.push(best.field);
  }
  // 批次 44：逐頁排除（行為屬 queryTailFields 接手渲染的欄位）。
  const filtered = picked.filter((field) => EXCLUDED_QUERY_FIELDS[`${pageId}¦${field.name}`] !== true);
  // 批次 43：label 逐字勘誤（FIELD_LABEL_OVERRIDES；generated 紅線不動）。
  const fixed = filtered.map((field) => {
    const label = FIELD_LABEL_OVERRIDES[`${pageId}¦${field.name}`];
    return label === undefined ? field : { ...field, label };
  });
  cache.set(pageId, fixed);
  return fixed.length === 0 ? undefined : fixed;
}

/** 該頁因為沒有資料來源而刻意不畫的欄位（C5-h 反向面的揭露用）。 */
export function skippedFieldsFor(pageId: string): readonly LegacyFieldControl[] {
  return (LEGACY_FIELDS[pageId] ?? []).filter(isSkipped);
}

const dynamicCache = new Map<string, readonly LegacyFieldOption[]>();

/**
 * W6（批次 42，issue #40）：**非首項**的寫死選項（per-page 附加）。
 * key＝`<pageId>¦<select name>`；插入在 seed 值之前（渲染端 prompt option 之後）。
 * 目前僅 account-v-2¦secondDepartmentId：legacy JS append 順序（accountV2.jsp:152-153）
 * 不包含次科室(0)→全部(-1)→DB 子科室——首項「不包含次科室」已由 SELECT_PROMPT_OVERRIDES
 * 鏡像，第二項「全部(-1)」是 override 表（單首項形狀，本批不動）表達不了的，故在此附加。
 */
const EXTRA_SELECT_OPTIONS: Readonly<Record<string, readonly LegacyFieldOption[]>> = {
  'config-account-v-2¦secondDepartmentId': [{ value: '-1', text: '全部' }],
};

/**
 * W9（批次 42，issue #40，裁決①B）：static select 初始值接 legacy `selected`。
 *
 * GNL-002 原慣例（批次 29）是「static select 的表單 state 保持 ''（未選語意），
 * 顯示與送出值差異記錄結案」；裁決①B 對本表命中欄**改判**：初始值直接接 legacy
 * selected 選項值——畫面顯示（select.value）與查詢送出值（filters）都與 legacy 一致。
 * option 的 selected **屬性**序列化差（legacy `<option value="1" selected>` vs React
 * 受控 select 的 property 語意）殘 1 筆/頁＝R8 canonical 工具批，勿 hack。
 *
 * 只收 4 頁（行號親驗）：account-v-2=啟用(1)（accountV2.jsp:655）、
 * department=-1（department.jsp:593）、job-title=-1（jobTitle.jsp:310）、
 * organization=-1（organization.jsp:359）。static isEnable 全站 11 條，
 * 其餘 7 條不在本批範圍（GNL-005-e/f 批 triage）。
 */
export const SELECT_DEFAULT_OVERRIDES: Readonly<Record<string, string>> = {
  'config-account-v-2¦isEnable': '1',
  'config-department¦isEnable': '-1',
  'config-job-title¦isEnable': '-1',
  'config-organization¦isEnable': '-1',
  // 批次 52（§8 裁決 C，值面「含」）：selOnly 家族（選項集兩側已同、僅 selected 態差）中
  // 非 deferred 者——isEnable 7 條屬 GNL-005-e/f triage 批不在本批；deviceType/locationType
  // 已 canonical 清；本批收 §8-C 例示的啟用態 5 鍵（static 列舉，W9 同款接線）：
  // form-co-edit-form-query-page¦enableType（jsp 啟用=ENABLE）、4 頁 enableStatus（啟用=true）。
  'form-co-edit-form-query-page¦enableType': 'ENABLE',
  'form-class-form-list¦enableStatus': 'true',
  'form-plan-form-list¦enableStatus': 'true',
  'customer-tsgh-to-high-risk-form¦enableStatus': 'true',
  'customer-tsgh-to-service-standard-form¦enableStatus': 'true',
  // 批次 52（裁定 C）dynamic 初始值群（§4/§6 逐欄表；渲染端 prompt/override 首值語意同步）：
  'form-plan-form-list¦annualId': '1',
  'form-plan-form-list¦organizationId': '1',
  'form-plan-form-list¦jobTypeId': '1',
  'form-plan-form-list¦formDepartmentId': '1',
  'form-plan-form-list¦formTemplateId': '-2',
  'form-co-edit-form-query-page¦annual': '1',
  'form-co-edit-form-query-page¦organization': '1',
  'form-co-edit-form-query-page¦jobType': '1',
  'form-co-edit-form-query-page¦formDepartment': '1',
  'course-clazz-query-list-class-query¦jobTypeId': '1',
  'course-clazz-query-list-class-query¦departmentId': '1',
  'course-clazz-query-list-class-query¦courseTypeId': '-1',
  // 【批次 52 勘誤】course-clazz-query-list¦organizationId 值面不接：該頁不在 §7.2 29 頁空表清單（泛用 12 列仍在、calendar 行為測試依賴），客戶端 matchesFilters 全等比對 organizationId='1' vs 泛用列 'org-1' 會清空列表＝收斂與行為衝突；值面登記遺留。
};

/** 查某頁某欄的 legacy selected 初始值；未登錄回 undefined（維持未選語意）。 */
export function selectDefaultFor(pageId: string, name: string): string | undefined {
  return SELECT_DEFAULT_OVERRIDES[`${pageId}¦${name}`];
}

/**
 * 批次 49（b49-planinst／規格 .tmp/tasks/orca-tasks/b49-planinst-spec.md §4.1/§4.4；Lead 裁定
 * .tmp/tasks/orca-tasks/b49-planinst-ruling-1.md 核准本檔為 hook 點）：dynamic select 的**整組**
 * 選項 override——鍵＝`pageId¦name`，值＝readonly LegacyFieldOption[]，**replace 語意**
 * （命中即整組取代 optionsFor 的 buildValues 假選項；EXTRA_SELECT_OPTIONS 是 prepend
 * 語意，表達不了「legacy 初始態 0 options」）。與 selectPromptFor 並存：首項 prompt
 * 照舊由渲染端畫，override 供第二項起；未命中的頁零影響。
 *
 * 本表收 training-plan-inst-plan-inst-list 查詢 form 9 個 dynamic select，對齊 legacy
 * 初始態（admin，raw-dom:1367-1435 實測）：annualId/organizationId/jobTypeId 各 1 筆真
 * 選項（115／本機帳號／系統管理），其餘 6 個（departmentId/planTargetId/phaseTargetId/
 * studentId/mentorId/clinicalId）＝0 options（空陣列；渲染端不畫 prompt option）。
 * departmentId 的 legacy form-group 帶 hidden class（raw-dom:1386，該群整組視覺隱藏）。
 * 值為【雛形值待實機校準】（種子值校準＋B-P2 放量，scratchpad 既有帳）。
 *
 * 附帶效果（有意）：isUserDrivable 走 optionsFor——0 options 欄位＝不可驅動（seed 帳），
 * C5-h 接線測試的 drive() 不再對 0-option select throw（contract-fields-wiring.test.tsx）。
 */
export const OPTION_SET_OVERRIDES: Readonly<Record<string, readonly LegacyFieldOption[]>> = {
  'training-plan-inst-plan-inst-list¦annualId': [{ value: '1', text: '115' }],
  'training-plan-inst-plan-inst-list¦organizationId': [{ value: '1', text: '本機帳號' }],
  'training-plan-inst-plan-inst-list¦jobTypeId': [{ value: '1', text: '系統管理' }],
  'training-plan-inst-plan-inst-list¦departmentId': [],
  'training-plan-inst-plan-inst-list¦planTargetId': [],
  'training-plan-inst-plan-inst-list¦phaseTargetId': [],
  'training-plan-inst-plan-inst-list¦studentId': [],
  'training-plan-inst-plan-inst-list¦mentorId': [],
  'training-plan-inst-plan-inst-list¦clinicalId': [],
  // ----------------------------------------------------------------
  // 批次 52（種子值校準，規格 .tmp/tasks/orca-tasks/b52-seed-spec.md §7.1；Lead 裁定 A~G）：
  // GNL-006 select 家族 rollout——94 頁 gap（v2 逐實例 410 條，b52-family-gap.json）經
  // .tmp/b52-codemod.mjs 產生 374 鍵（排除：5 剔除頁 16 條目【勘誤 b52-calibrate3：初稿誤記 17】／
  // D1 態差 leave-leave-form¦planId／
  // 凍結重複選項 attendance-export¦departmentId（裁定 G 僅核准 vote NaN，重複不鏡像）／selOnly 20 條）。
  // 值源＝凍結 diff-relevant 實例（裁定 F 以凍結為準；D1/D2/D3 漂移鍵不灌 live 值）。
  // prompt-hit 鍵（SELECT_PROMPT_OVERRIDES 命中且 frozen[0]==prompt）已剔除首項 prompt 選項
  // （渲染端 prompt 照畫，override 供第二項起）；無 prompt 鍵＝全集，靠渲染端規則 A
  // （非空 override 無 prompt → 不畫預設空 option，§8-A）對齊；真空鍵＝[]（0 options，
  // planinst 先例：連 prompt 都不畫）。
  // 批次 52 修訂（b52-calibrate3，回應 b51-review-14 F-1）：8 個 prompt-hit 後為空的鍵
  // （凍結僅 prompt 一項）改帶回該選項（[{prompt 同款}]）——渲染端 C2 去重不另畫 prompt，
  // override 渲染＝凍結全集＝零殘差；規則①（[]＝真真空連 prompt 都不畫）語意不動。
  // 批次 52 修訂 v2（b52-calibrate5，回應 b51-review-14b F-14b-1）：43 個「prompt-hit-slice1＋
  // 自畫接線消費」鍵改帶回 prompt 同款（override=凍結全集）——自畫渲染端不補畫 prompt
  // （override 命中即去 placeholder），slice(1) 會使渲染集=凍結全集−首項（凍結 N→react N−1）；
  // LCF 消費的 38 鍵維持 slice(1)（LegacyContractFields 補畫 prompt＋displayValue 對齊
  // prompt.value＝凍結忠實；帶回反而 promptDupInOverride 翻真＝顯示倒退）。
  // ----------------------------------------------------------------
  'announcement-announcement-list¦refId': [{ value: '-1', text: '全部' }],
  'form-co-edit-form-completion-statistics-page¦annual': [{ value: '1', text: '115' }],
  'form-co-edit-form-completion-statistics-page¦organization': [{ value: '1', text: '本機帳號' }],
  'form-co-edit-form-completion-statistics-page¦jobType': [{ value: '', text: '請選擇' }, { value: '1', text: '系統管理' }],
  'form-co-edit-form-completion-statistics-page¦planTarget': [],
  'form-co-edit-form-completion-statistics-page¦formDepartment': [{ value: '-1', text: '全部' }, { value: '1', text: '管理部門' }],
  'form-co-edit-form-completion-statistics-page¦formTemplate': [{ value: '0', text: '全部' }],
  'form-co-edit-form-export-page¦annual': [{ value: '1', text: '115' }],
  'form-co-edit-form-export-page¦organization': [{ value: '1', text: '本機帳號' }],
  'form-co-edit-form-export-page¦jobType': [{ value: '', text: '請選擇' }, { value: '1', text: '系統管理' }],
  'form-co-edit-form-export-page¦planTarget': [],
  'form-co-edit-form-export-page¦formDepartment': [{ value: '1', text: '管理部門' }],
  'form-co-edit-form-export-page¦formTemplate': [],
  'form-co-edit-form-form-generator¦organization': [{ value: '1', text: '本機帳號' }],
  'form-co-edit-form-form-generator¦jobType': [{ value: '1', text: '系統管理' }],
  'form-co-edit-form-form-generator¦formDeptId': [{ value: '1', text: '管理部門' }],
  'form-co-edit-form-form-generator¦formtemplateId': [],
  'form-co-edit-form-query-page¦annual': [{ value: '1', text: '115' }],
  'form-co-edit-form-query-page¦organization': [{ value: '1', text: '本機帳號' }],
  'form-co-edit-form-query-page¦jobType': [{ value: '1', text: '系統管理' }],
  'form-co-edit-form-query-page¦planTarget': [],
  'form-co-edit-form-query-page¦formDepartment': [{ value: '1', text: '管理部門' }],
  'form-co-edit-form-query-page¦formTemplate': [],
  // 批次 52 C2 特例：prompt=(0,未設定) 在凍結序列位次 2（凍結=[本機帳號(1),未設定(0)]）——
  // override 帶全凍結集，渲染端批次 52 去重規則（override 已含 prompt 同款→不另畫 prompt）＝零殘差。
  'config-account-v-2¦organizationId': [{ value: '1', text: '本機帳號' }, { value: '0', text: '未設定' }],
  'config-account-v-2¦jobTypeId': [{ value: '1', text: '系統管理' }],
  'config-account-v-2¦departmentId': [{ value: '1', text: '管理部門' }],
  'config-account-v-2¦secondDepartmentId': [{ value: '-1', text: '全部' }],
  'config-account-v-2¦roleId': [{ value: '1', text: '系統管理員' }, { value: '2', text: '教學管理單位' }, { value: '3', text: '科部管理員' }, { value: '4', text: '授課老師' }, { value: '5', text: '學員' }, { value: '6', text: '導師' }, { value: '7', text: '臨床教師' }, { value: '8', text: '計畫主持人' }, { value: '9', text: '單位主管' }],
  'config-device¦orgName': [{ value: '1', text: '本機帳號' }],
  'config-device¦departmentId': [{ value: '0', text: '共用' }, { value: '1', text: '管理部門' }],
  'config-job-title¦organizationId': [{ value: '1', text: '本機帳號' }],
  'config-location¦orgName': [{ value: '1', text: '本機帳號' }],
  'config-location¦departmentId': [{ value: '0', text: '共用' }, { value: '1', text: '管理部門' }],
  'course-clazz-list¦organizationId': [{ value: '1', text: '本機帳號' }],
  'course-clazz-list¦jobTypeId': [],
  'course-clazz-list¦departmentId': [],
  'course-clazz-list¦kind': [{ value: '', text: '全部' }],
  'course-clazz-list¦courseTypeId': [],
  'course-clazz-query-list-class-query¦organizationId': [{ value: '1', text: '本機帳號' }],
  'course-clazz-query-list-class-query¦kind': [{ value: '', text: '全部' }],
  'course-clazz-query-list-class-query¦jobTypeId': [{ value: '1', text: '系統管理' }],
  'course-clazz-query-list-class-query¦departmentId': [{ value: '1', text: '管理部門' }],
  'course-clazz-query-list-class-query¦courseTypeId': [{ value: '-1', text: '全部' }],
  'course-clazz-query-list¦organizationId': [{ value: '1', text: '本機帳號' }],
  'course-clazz-query-list¦kind': [{ value: '', text: '全部' }],
  'course-clazz-query-list¦jobTypeId': [],
  'course-clazz-query-list¦departmentId': [],
  'course-clazz-query-list¦courseTypeId': [],
  'course-course-manage¦organizationId': [{ value: '1', text: '本機帳號' }],
  'course-course-manage¦jobTypeId': [{ value: '1', text: '系統管理' }],

  'course-course-manage¦departmentId': [{ value: '1', text: '管理部門' }],
  'course-course-manage¦kind': [{ value: '', text: '全部' }],
  'course-course-manage¦courseTypeId': [{ value: '-1', text: '全部' }],
  'course-course-type¦organizationId': [{ value: '1', text: '本機帳號' }],
  'course-course-type¦jobTypeId': [{ value: '1', text: '系統管理' }],
  'course-course-type¦departmentId': [{ value: '0', text: '共用' }, { value: '1', text: '管理部門' }],
  'course-course-type¦kind': [{ value: '', text: '全部' }],
  'form-class-form-list¦annualId': [{ value: '0', text: '全部' }],
  'form-class-form-list¦organizationId': [{ value: '1', text: '本機帳號' }],
  'form-class-form-list¦jobTypeId': [{ value: '1', text: '系統管理' }],
  'form-class-form-list¦departmentId': [{ value: '1', text: '管理部門' }],
  'form-class-form-list¦kind': [{ value: '', text: '全部' }],
  'form-class-form-list¦courseTypeId': [{ value: '-1', text: '全部' }],
  'form-class-form-list¦formDepartmentId': [{ value: '', text: '請選擇' }, { value: '1', text: '管理部門' }],
  'form-class-form-list¦formTemplateId': [],
  'form-class-form-list¦fillerId': [],
  'form-class-form-list¦studentId': [],
  'instruction-course-department-code¦organizationId': [{ value: '1', text: '本機帳號' }],
  'report-common-class-form-export¦organizationId': [{ value: '1', text: '本機帳號' }],
  'report-common-class-form-export¦jobTypeId': [{ value: '', text: '請選擇' }, { value: '1', text: '系統管理' }],
  'report-common-class-form-export¦departmentId': [{ value: '-1', text: '全部' }, { value: '1', text: '管理部門' }],
  'report-common-class-form-export¦kind': [{ value: '', text: '全部' }],
  'report-common-class-form-export¦courseTypeId': [],
  'report-common-class-form-export¦formDepartmentId': [{ value: '-999', text: '共用表單' }, { value: '', text: '請選擇' }, { value: '1', text: '管理部門' }],
  'report-common-class-form-export¦formTemplateId': [],
  'discuss-discuss-board¦organizationId': [{ value: '1', text: '本機帳號' }],
  'discuss-discuss-board¦jobTypeId': [{ value: '1', text: '系統管理' }],
  'discuss-discuss-board¦discussTypeId': [{ value: '0', text: '全部' }],
  'discuss-discuss-type¦organizationId': [{ value: '1', text: '本機帳號' }],
  'discuss-discuss-type¦jobTypeId': [{ value: '1', text: '系統管理' }],
  'exam-dispatch-exam¦annualId': [{ value: '1', text: '115' }],
  'exam-dispatch-exam¦organizationId': [{ value: '1', text: '本機帳號' }],
  'exam-dispatch-exam¦jobTypeId': [{ value: '1', text: '系統管理' }],
  'exam-dispatch-exam¦departmentId': [],
  'exam-dispatch-exam¦planTargetId': [],
  'exam-dispatch-exam¦phaseTargetId': [],
  'exam-dispatch-exam¦examTargetId': [{ value: '-2', text: '無資料' }],
  'exam-exam-manage¦organizationId': [{ value: '1', text: '本機帳號' }],
  'exam-exam-manage¦jobTypeId': [{ value: '1', text: '系統管理' }],
  'exam-exam-manage¦departmentId': [{ value: '1', text: '管理部門' }],
  'exam-question-bank-type¦organizationId': [{ value: '1', text: '本機帳號' }],
  'exam-question-bank-type¦departmentId': [{ value: '1', text: '管理部門' }],
  'exam-question-bank¦organizationId': [{ value: '1', text: '本機帳號' }],
  'exam-question-bank¦jobTypeId': [{ value: '1', text: '系統管理' }],
  'exam-question-bank¦departmentId': [{ value: '1', text: '管理部門' }],
  'exam-testing-manage¦annualId': [{ value: '1', text: '115' }],
  'exam-testing-manage¦organizationId': [{ value: '1', text: '本機帳號' }],
  'exam-testing-manage¦jobTypeId': [{ value: '1', text: '系統管理' }],
  'exam-testing-manage¦departmentId': [],
  'exam-testing-manage¦planTargetId': [],

  'exam-testing-manage¦phaseTargetId': [],
  'exam-testing-manage¦examTargetId': [{ value: '-2', text: '無資料' }],
  'form-template-common-form-template¦organizationId': [{ value: '1', text: '本機帳號' }],
  'form-template-common-form-template¦jobTypeId': [{ value: '', text: '請選擇' }, { value: '1', text: '系統管理' }],
  'form-template-field-type-list¦organizationId': [{ value: '1', text: '本機帳號' }],
  'form-template-field-type-list¦jobTypeId': [{ value: '-1', text: '系統內建範例' }, { value: '', text: '請選擇' }, { value: '1', text: '系統管理' }],
  'form-template-field-type-list¦type': [{ value: 'SelectType', text: '下拉選單型' }],
  'form-template-form-template-list¦organizationId': [{ value: '1', text: '本機帳號' }],
  'form-template-form-template-list¦jobTypeId': [{ value: '1', text: '系統管理' }],
  'form-template-form-template-list¦departmentId': [{ value: '-1', text: '全部' }, { value: '1', text: '管理部門' }],
  'form-template-form-template-list¦formTypeId': [{ value: '0', text: '全部' }, { value: '1', text: '(測試) 一般表單' }],
  'form-template-form-template-list¦purpose': [{ value: '', text: '全部' }, { value: 'plan', text: '訓練計畫' }, { value: 'class', text: '課程' }, { value: 'studyItem', text: '學習項目' }, { value: 'co-editor-form', text: '共同編輯' }],
  'form-template-form-template-list¦verifyStatus': [{ value: 'ALL', text: '全部' }, { value: '', text: '未審核' }, { value: 'NOT', text: '審核中' }, { value: 'PASS', text: '通過' }, { value: 'FAIL', text: '未通過' }],
  'form-template-form-template-list¦enableStatus': [{ value: '-1', text: '全部' }, { value: '1', text: '啟用' }, { value: '0', text: '停用' }],
  'form-template-form-template¦purpose': [{ value: '', text: '' }, { value: 'plan', text: '訓練計畫' }, { value: 'class', text: '課程/會議' }, { value: 'studyItem', text: '學習項目' }, { value: 'co-editor-form', text: '共同編輯' }],
  'form-template-form-template¦formTypeId': [{ value: '1', text: '(測試) 一般表單' }],
  'instruction-teacher-pool-list¦organizationId': [{ value: '1', text: '本機帳號' }],
  'instruction-teacher-pool-list¦jobTypeId': [{ value: '1', text: '系統管理' }],
  'instruction-teacher-pool-list¦departmentId': [{ value: '1', text: '管理部門' }],
  'instruction-plan-target-plan-target-list¦annualId': [{ value: '1', text: '115' }],
  'instruction-plan-target-plan-target-list¦organizationId': [],
  'instruction-plan-target-plan-target-list¦jobTypeId': [],
  'instruction-plan-target-plan-target-list¦departmentId': [],
  'instruction-study-item-maintain¦organizationId': [{ value: '1', text: '本機帳號' }],
  'instruction-study-item-maintain¦jobTypeId': [{ value: '1', text: '系統管理' }],
  'instruction-study-item-maintain¦departmentId': [{ value: '1', text: '管理部門' }],
  'instruction-study-item-maintain¦studyItemTypeId': [],
  'instruction-study-item-type-maintain¦organizationId': [{ value: '1', text: '本機帳號' }],
  'instruction-study-item-type-maintain¦jobTypeId': [{ value: '1', text: '系統管理' }],
  'instruction-study-item-type-maintain¦departmentId': [{ value: '1', text: '管理部門' }],
  'config-job-type-department¦queryOrgId': [{ value: '1', text: '本機帳號' }],
  'config-job-type¦organizationId': [{ value: '1', text: '本機帳號' }],
  'leave-leave-annual¦annualId': [{ value: '1', text: '115' }],
  'leave-leave-annual¦organizationId': [{ value: '1', text: '本機帳號' }],
  'leave-leave-annual¦jobTypeId': [{ value: '1', text: '系統管理' }],
  'leave-leave-annual¦planTargetId': [],
  'leave-leave-annual¦departmentId': [],
  'leave-leave-annual¦studentId': [],
  'leave-leave-form¦annualId': [{ value: '1', text: '115' }],
  'leave-leave-form¦organizationId': [{ value: '1', text: '本機帳號' }],
  'leave-leave-form¦jobTypeId': [{ value: '1', text: '系統管理' }],
  'leave-leave-form¦departmentId': [],
  'leave-leave-form¦accountId': [],
  'leave-leave-type¦organizationId': [{ value: '1', text: '本機帳號' }],
  'leave-leave-type¦jobTypeId': [{ value: '1', text: '系統管理' }],
  'config-comment¦organizationId': [{ value: '1', text: '本機帳號' }],
  'config-comment¦jobTypeId': [{ value: '1', text: '系統管理' }],
  'config-comment¦departmentId': [{ value: '1', text: '管理部門' }],
  'profile-profile-maintenance¦locationId': [{ value: '0', text: '無' }],
  'course-course-static-list¦annualId': [{ value: '1', text: '115' }],

  'course-course-static-list¦organizationId': [{ value: '1', text: '本機帳號' }],
  'course-course-static-list¦jobTypeId': [{ value: '-2', text: '全部' }, { value: '1', text: '系統管理' }],
  'course-course-static-list¦departmentId': [{ value: '-1', text: '請選擇' }, { value: '0', text: '共用' }, { value: '1', text: '管理部門' }],
  'course-course-static-list¦kind': [{ value: '', text: '全部' }],
  'form-form-static-list¦annualId': [{ value: '1', text: '115' }],
  'form-form-static-list¦organizationId': [{ value: '1', text: '本機帳號' }],
  'form-form-static-list¦jobtypeId': [{ value: '-1', text: '請選擇' }, { value: '1', text: '系統管理' }],
  'form-form-static-list¦planTargetId': [],
  'form-form-static-list¦departmentId': [{ value: '-1', text: '請選擇' }, { value: '0', text: '共用' }, { value: '1', text: '管理部門' }],
  'form-form-static-list¦studentId': [],
  'report-common-clazz-attendance-export¦organizationId': [{ value: '1', text: '本機帳號' }],
  'report-common-clazz-attendance-export¦jobTypeId': [{ value: '1', text: '系統管理' }],
  'report-common-clazz-attendance-export¦kind': [{ value: '', text: '全部' }],
  'report-common-clazz-attendance-export¦courseTypeId': [{ value: '-1', text: '全部' }],
  'report-common-clazz-detail-export¦organizationId': [{ value: '1', text: '本機帳號' }],
  'report-common-clazz-detail-export¦jobTypeId': [{ value: '1', text: '系統管理' }],
  'report-common-clazz-detail-export¦departmentId': [{ value: '-1', text: '全部' }, { value: '1', text: '管理部門' }],
  'report-common-clazz-detail-export¦kind': [{ value: '', text: '全部' }],
  'report-common-clazz-detail-export¦courseTypeId': [{ value: '-1', text: '全部' }],
  'report-pgywm-all-student-scores¦annualId': [{ value: '1', text: '115' }],
  'report-pgywm-all-student-scores¦organizationId': [{ value: '1', text: '本機帳號' }],
  'report-pgywm-all-student-scores¦studentId': [],
  'report-tsgh-academic-mom¦departmentId': [{ value: '-2', text: '無資料' }],
  'report-tsgh-class-form-static-to-class-form-7¦teacherId': [{ value: '', text: '請選擇' }],
  'report-tsgh-class-form-static-to-class-form-7¦classId': [{ value: '', text: '請選擇' }],
  'report-tsgh-class-form-static¦teacherId': [{ value: '', text: '請選擇' }],
  'report-tsgh-class-form-static¦classId': [{ value: '', text: '請選擇' }],
  'report-tsgh-core-skill-form-report¦annualId': [{ value: '1', text: '115' }],
  'report-tsgh-core-skill-form-report¦organizationId': [{ value: '1', text: '本機帳號' }],
  'report-tsgh-core-skill-form-report¦jobTypeId': [{ value: '', text: '請選擇' }, { value: '1', text: '系統管理' }],
  'report-tsgh-core-skill-form-report¦departmentId': [{ value: '-1', text: '全部' }, { value: '1', text: '管理部門' }],
  'report-tsgh-core-skill-form-report¦planTargetId': [],
  'report-tsgh-core-skill-form-report¦annualId2': [{ value: '1', text: '115' }],
  'report-tsgh-core-skill-form-report¦organizationId2': [{ value: '1', text: '本機帳號' }],
  'report-tsgh-core-skill-form-report¦jobTypeId2': [{ value: '', text: '請選擇' }, { value: '1', text: '系統管理' }],
  'report-tsgh-core-skill-form-report¦departmentId2': [{ value: '-1', text: '全部' }, { value: '1', text: '管理部門' }],
  'report-tsgh-core-skill-form-report¦planTargetId2': [],
  'report-tsgh-core-skill-report-by-form¦annualId': [],
  'report-tsgh-core-skill-report-by-form¦organizationId': [],
  'report-tsgh-core-skill-report-by-form¦jobTypeId': [],
  'report-tsgh-core-skill-report-by-student¦annualId': [],
  'report-tsgh-core-skill-report-by-student¦organizationId': [],
  'report-tsgh-core-skill-report-by-student¦jobTypeId': [],
  'report-tsgh-core-skill-statistic¦annualId': [{ value: '1', text: '115' }],
  'report-tsgh-core-skill-statistic¦organizationId': [{ value: '1', text: '本機帳號' }],
  'report-tsgh-core-skill-statistic¦jobTypeId': [{ value: '', text: '請選擇' }, { value: '1', text: '系統管理' }],
  'report-tsgh-core-skill-statistic¦departmentId': [{ value: '-1', text: '全部' }, { value: '1', text: '管理部門' }],
  'report-tsgh-core-skill-statistic¦planTargetId': [],
  'report-tsgh-core-skill-statistic¦annualId2': [{ value: '1', text: '115' }],
  'report-tsgh-core-skill-statistic¦organizationId2': [{ value: '1', text: '本機帳號' }],

  'report-tsgh-core-skill-statistic¦jobTypeId2': [{ value: '', text: '請選擇' }, { value: '1', text: '系統管理' }],
  'report-tsgh-core-skill-statistic¦departmentId2': [{ value: '-1', text: '全部' }, { value: '1', text: '管理部門' }],
  'report-tsgh-core-skill-statistic¦planTargetId2': [],
  'report-tsgh-core-skill-sutdent-report¦annualId': [{ value: '1', text: '115' }],
  'report-tsgh-core-skill-sutdent-report¦organizationId': [{ value: '1', text: '本機帳號' }],
  'report-tsgh-core-skill-sutdent-report¦jobTypeId': [{ value: '', text: '請選擇' }, { value: '1', text: '系統管理' }],
  'report-tsgh-core-skill-sutdent-report¦departmentId': [{ value: '-1', text: '全部' }, { value: '1', text: '管理部門' }],
  'report-tsgh-core-skill-sutdent-report¦planTargetId': [],
  'report-tsgh-core-skill-sutdent-report¦annualId2': [{ value: '1', text: '115' }],
  'report-tsgh-core-skill-sutdent-report¦organizationId2': [{ value: '1', text: '本機帳號' }],
  'report-tsgh-core-skill-sutdent-report¦jobTypeId2': [{ value: '', text: '請選擇' }, { value: '1', text: '系統管理' }],
  'report-tsgh-core-skill-sutdent-report¦departmentId2': [{ value: '-1', text: '全部' }, { value: '1', text: '管理部門' }],
  'report-tsgh-core-skill-sutdent-report¦planTargetId2': [],
  'report-tsgh-gpa-form-report¦annualId': [{ value: '1', text: '115' }],
  'report-tsgh-gpa-form-report¦organizationId': [{ value: '1', text: '本機帳號' }],
  'report-tsgh-gpa-form-report¦formTemplateId': [{ value: '-1', text: '請選擇' }],
  'report-tsgh-plan-form-static-2-to-plan-form-10¦phaseDepartmentId': [{ value: '', text: '全部' }, { value: '1', text: '管理部門' }],
  'report-tsgh-plan-form-static-2-to-plan-form-9¦phaseDepartmentId': [{ value: '', text: '全部' }, { value: '1', text: '管理部門' }],
  'report-tsgh-plan-form-static-2¦phaseDepartmentId': [{ value: '', text: '全部' }, { value: '1', text: '管理部門' }],
  'report-tsgh-plan-form-static-to-plan-form-5¦teacherId': [{ value: '', text: '請選擇' }],
  'report-tsgh-plan-form-static¦teacherId': [{ value: '', text: '請選擇' }],
  'report-ugywm-course-form-finishrate¦departmentId': [{ value: '-1', text: '全部' }, { value: '1', text: '管理部門' }],
  'report-ugywm-course-form-finishrate¦kind': [],
  'report-ugywm-course-form-finishrate¦courseTypeId': [],
  'report-ugywm-student-dept-satisfy¦annualId': [{ value: '0', text: '全部' }, { value: '1', text: '115' }],
  'vote-total-vote-analysis-report¦annualId': [{ value: '1', text: '115' }],
  'vote-total-vote-analysis-report¦organizationId': [{ value: '1', text: '本機帳號' }],
  'vote-total-vote-analysis-report¦voteId': [],
  'vote-total-vote-report¦annualId': [{ value: '1', text: '115' }],
  'vote-total-vote-report¦organizationId': [{ value: '1', text: '本機帳號' }],
  'vote-total-vote-report¦voteId': [],
  'customer-tsgh-tsgh-study-history-list¦annualId': [{ value: '1', text: '115' }],
  'customer-tsgh-tsgh-study-history-list¦organizationId': [{ value: '1', text: '本機帳號' }],
  'customer-tsgh-tsgh-study-history-list¦jobTypeId': [{ value: '1', text: '系統管理' }],
  'customer-tsgh-tsgh-study-history-list¦departmentId': [],
  'customer-tsgh-tsgh-study-history-list¦planTargetId': [],
  'customer-tsgh-tsgh-study-history-list¦phaseTargetId': [],
  'customer-tsgh-tsgh-study-history-list¦studentId': [],
  'customer-tsgh-case-report¦organizationId': [{ value: '1', text: '本機帳號' }],
  'customer-tsgh-case-report¦jobTypeId': [{ value: '', text: '請選擇' }, { value: '1', text: '系統管理' }],
  'customer-tsgh-case-report¦departmentId': [],
  'customer-tsgh-case-report¦annualId': [{ value: '1', text: '115' }],
  'customer-tsgh-case-report¦planTargetId': [],
  'customer-tsgh-case-report¦phaseDepartmentId': [],
  'customer-tsgh-case-report¦phaseTargetId': [],
  'customer-tsgh-case-report¦studentId': [],
  'customer-tsgh-clert-cerk¦annualId': [{ value: '1', text: '115' }],
  'customer-tsgh-clert-cerk¦organizationId': [{ value: '1', text: '本機帳號' }],
  'customer-tsgh-clert-cerk¦jobTypeId': [{ value: '', text: '請選擇' }, { value: '1', text: '系統管理' }],
  'customer-tsgh-clert-cerk¦planTargetId': [],

  'customer-tsgh-clert-cerk¦studentId': [],
  'customer-tsgh-core-curriculum-report¦annualId': [{ value: '1', text: '115' }],
  'customer-tsgh-core-curriculum-report¦organizationId': [{ value: '1', text: '本機帳號' }],
  'customer-tsgh-core-curriculum-report¦jobTypeId': [{ value: '', text: '請選擇' }, { value: '1', text: '系統管理' }],
  'customer-tsgh-core-curriculum-report¦departmentId': [],
  'customer-tsgh-core-curriculum-report¦planTargetId': [],
  'customer-tsgh-core-curriculum-report¦groupId': [],
  'customer-tsgh-core-curriculum-report¦studentId': [],
  'customer-tsgh-course-record-report¦organizationId': [{ value: '1', text: '本機帳號' }],
  'customer-tsgh-course-record-report¦jobTypeId': [{ value: '', text: '請選擇' }, { value: '1', text: '系統管理' }],
  'customer-tsgh-course-record-report¦departmentId': [],
  'customer-tsgh-course-record-report¦annualId': [{ value: '1', text: '115' }],
  'customer-tsgh-course-record-report¦planTargetId': [],
  'customer-tsgh-course-record-report¦phaseDepartmentId': [],
  'customer-tsgh-course-record-report¦phaseTargetId': [],
  'customer-tsgh-course-record-report¦studentId': [],
  'customer-tsgh-tsgh-student-course-report¦organizationId': [{ value: '1', text: '本機帳號' }],
  'customer-tsgh-tsgh-student-course-report¦jobTypeId': [{ value: '', text: '請選擇' }, { value: '1', text: '系統管理' }],
  'customer-tsgh-tsgh-student-course-report¦departmentId': [],
  'customer-tsgh-tsgh-student-course-report¦annualId': [{ value: '1', text: '115' }],
  'customer-tsgh-tsgh-student-course-report¦planTargetId': [],
  'customer-tsgh-tsgh-student-course-report¦phaseDepartmentId': [],
  'customer-tsgh-tsgh-student-course-report¦phaseTargetId': [],
  'customer-tsgh-tsgh-student-course-report¦studentId': [],
  'form-all-form-list¦annualId': [{ value: '1', text: '115' }],
  'form-all-form-list¦organizationId': [{ value: '1', text: '本機帳號' }],
  'form-all-form-list¦jobtypeId': [{ value: '-1', text: '請選擇' }, { value: '1', text: '系統管理' }],
  'form-all-form-list¦planTargetId': [],
  'form-all-form-list¦departmentId': [{ value: '-1', text: '請選擇' }, { value: '0', text: '共用' }, { value: '1', text: '管理部門' }],
  'form-all-form-list¦studentId': [],
  'form-plan-form-batch-send¦annualId': [{ value: '1', text: '115' }],
  'form-plan-form-batch-send¦organizationId': [{ value: '1', text: '本機帳號' }],
  'form-plan-form-batch-send¦jobTypeId': [{ value: '', text: '請選擇' }, { value: '1', text: '系統管理' }],
  'form-plan-form-batch-send¦departmentId': [],
  'form-plan-form-batch-send¦planTargetId': [],
  'form-plan-form-batch-send¦phaseDepartmentId': [],
  'form-plan-form-batch-send¦phaseTargetId': [],
  'form-plan-form-batch-send¦formTemplateId': [],
  'form-plan-form-list¦annualId': [{ value: '1', text: '115' }],
  'form-plan-form-list¦organizationId': [{ value: '1', text: '本機帳號' }],
  'form-plan-form-list¦jobTypeId': [{ value: '1', text: '系統管理' }],
  'form-plan-form-list¦departmentId': [],
  'form-plan-form-list¦planTargetId': [],
  'form-plan-form-list¦phaseTargetId': [],
  'form-plan-form-list¦phaseDepartmentId': [],
  'form-plan-form-list¦formDepartmentId': [{ value: '-1', text: '全部' }, { value: '1', text: '管理部門' }],
  'form-plan-form-list¦formTemplateId': [{ value: '-2', text: '無資料' }],
  'form-plan-form-list¦fillerId': [],
  'form-plan-form-list¦studentId': [],
  'report-common-plan-form-export¦annualId': [{ value: '1', text: '115' }],

  'report-common-plan-form-export¦organizationId': [{ value: '1', text: '本機帳號' }],
  'report-common-plan-form-export¦jobTypeId': [{ value: '', text: '請選擇' }, { value: '1', text: '系統管理' }],
  'report-common-plan-form-export¦departmentId': [{ value: '-1', text: '全部' }, { value: '1', text: '管理部門' }],
  'report-common-plan-form-export¦planTargetId': [],
  'report-common-plan-form-export¦phaseTargetId': [],
  'report-common-plan-form-export¦phaseDepartmentId': [],
  'report-common-plan-form-export¦formDepartmentId': [{ value: '-999', text: '共用表單' }, { value: '-1', text: '全部' }, { value: '1', text: '管理部門' }],
  'report-common-plan-form-export¦formTemplateId': [],
  'training-arrange-train¦annualId': [{ value: '1', text: '115' }],
  'training-arrange-train¦organizationId': [{ value: '1', text: '本機帳號' }],
  'training-arrange-train¦jobTypeId': [{ value: '', text: '請選擇' }, { value: '1', text: '系統管理' }],
  'training-arrange-train¦planTargetId': [],
  'training-arrange-train¦phaseDepartmentId': [],
  'training-arrange-train¦phaseTargetId': [],
  'training-arrange-train¦studentId': [],
  'training-arrange-train¦clinicalId': [],
  'training-arrange-train¦mentorId': [],
  'training-permission-manage-student¦organizationId': [{ value: '1', text: '本機帳號' }],
  'training-permission-manage-student¦jobTypeId': [{ value: '1', text: '系統管理' }],
  'training-permission-manage-student¦departmentId': [{ value: '1', text: '管理部門' }],
  'training-permission-manage-student¦planTargetId': [],
  'training-permission-manage-student¦roleId': [{ value: '5', text: '學員' }],
  'training-permission-manage-student¦accountId': [],
  'training-study-passport-study-passport-list¦annualId': [{ value: '1', text: '115' }],
  'training-study-passport-study-passport-list¦organizationId': [{ value: '1', text: '本機帳號' }],
  'training-study-passport-study-passport-list¦jobTypeId': [{ value: '1', text: '系統管理' }],
  'training-study-passport-study-passport-list¦departmentId': [],
  'training-study-passport-study-passport-list¦planTargetId': [],
  'training-study-passport-study-passport-list¦phaseTargetId': [],
  'training-study-passport-study-passport-list¦studentId': [],
  'training-transcript-list¦annualId': [{ value: '1', text: '115' }],
  'training-transcript-list¦organizationId': [{ value: '1', text: '本機帳號' }],
  'training-transcript-list¦jobTypeId': [{ value: '', text: '請選擇' }, { value: '1', text: '系統管理' }],
  'training-transcript-list¦planTargetId': [],
  'training-transcript-list¦clinicalDeptId': [{ value: '-1', text: '全部' }, { value: '1', text: '管理部門' }],
  'training-transcript-list¦phaseDepartmentId': [],
  'training-transcript-list¦phaseTargetId': [],
  'training-transcript-list¦userId': [],
  'customer-tsgh-high-risk-doctor-report¦organizationId': [{ value: '1', text: '本機帳號' }],
  'customer-tsgh-high-risk-doctor-report¦departmentId': [{ value: '0', text: '全部' }, { value: '1', text: '管理部門' }],
  'customer-tsgh-high-risk-report¦organizationId': [{ value: '1', text: '本機帳號' }],
  'customer-tsgh-high-risk-report¦departmentId': [{ value: '0', text: '全部' }, { value: '1', text: '管理部門' }],
  'customer-tsgh-high-risk-setting-report¦organizationId': [{ value: '1', text: '本機帳號' }],
  'customer-tsgh-high-risk-setting-report¦departmentId': [{ value: '-1', text: '全部' }, { value: '1', text: '管理部門' }],
  'customer-tsgh-high-risk-setting¦organizationId': [{ value: '1', text: '本機帳號' }],
  'customer-tsgh-high-risk-setting¦departmentId': [{ value: '-1', text: '請選擇' }, { value: '0', text: '共用' }, { value: '1', text: '管理部門' }],
  'customer-tsgh-service-standard-cfg-report¦organizationId': [{ value: '1', text: '本機帳號' }],
  'customer-tsgh-service-standard-cfg-report¦departmentId': [{ value: '0', text: '全部' }, { value: '1', text: '管理部門' }],
  'customer-tsgh-service-standard-report¦organizationId': [{ value: '1', text: '本機帳號' }],
  'customer-tsgh-service-standard-report¦departmentId': [{ value: '0', text: '全部' }, { value: '1', text: '管理部門' }],

  'customer-tsgh-service-standard-setting¦organizationId': [{ value: '1', text: '本機帳號' }],
  'customer-tsgh-service-standard-setting¦departmentId': [{ value: '-1', text: '請選擇' }, { value: '0', text: '共用' }, { value: '1', text: '管理部門' }],
  'customer-tsgh-tch-satisfaction-survey-report¦annualId': [{ value: '1', text: '115' }],
  'customer-tsgh-tch-satisfaction-survey-report¦organizationId': [{ value: '1', text: '本機帳號' }],
  'customer-tsgh-tch-satisfaction-survey-report¦jobTypeId': [],
  'customer-tsgh-tch-satisfaction-survey-report¦departmentId': [],
  'customer-tsgh-tch-satisfaction-survey-report¦planTargetId': [],
  'customer-tsgh-to-high-risk-form¦organizationId': [],
  'customer-tsgh-to-high-risk-form¦departmentId': [],
  'customer-tsgh-to-service-standard-form¦organizationId': [],
  'customer-tsgh-to-service-standard-form¦departmentId': [],
  'training-permission-manage-teacher¦organizationId': [{ value: '1', text: '本機帳號' }],
  'training-permission-manage-teacher¦jobTypeId': [{ value: '1', text: '系統管理' }],
  'training-permission-manage-teacher¦departmentId': [{ value: '1', text: '管理部門' }],
  'training-permission-manage-teacher¦roleId': [{ value: '2', text: '教學管理單位' }, { value: '3', text: '科部管理員' }, { value: '6', text: '導師' }, { value: '7', text: '臨床教師' }, { value: '8', text: '計畫主持人' }, { value: '9', text: '單位主管' }],
  'training-plan-inst-clinical-arrange¦annualId': [{ value: '1', text: '115' }],
  'training-plan-inst-clinical-arrange¦organizationId': [{ value: '1', text: '本機帳號' }],
  'training-plan-inst-clinical-arrange¦jobTypeId': [{ value: '1', text: '系統管理' }],
  'training-plan-inst-clinical-arrange¦planTargetId': [],
  'training-plan-inst-clinical-arrange¦clinicalDepartmentId': [{ value: '0', text: '共用' }, { value: '1', text: '管理部門' }],
  'vote-dept-manager-recommend¦annualId': [{ value: '-1', text: 'NaN' }, { value: '1', text: '115' }],
  'vote-dept-manager-recommend¦organizationId': [{ value: '1', text: '本機帳號' }],
  'vote-vote-maintenance¦annualId': [{ value: '-1', text: 'NaN' }, { value: '1', text: '115' }],
  'vote-vote-maintenance¦organizationId': [{ value: '1', text: '本機帳號' }],
};

/** 查某頁某欄的整組選項 override；未登錄回 undefined（走既有假選項路徑）。 */
export function optionSetFor(pageId: string, name: string): readonly LegacyFieldOption[] | undefined {
  return OPTION_SET_OVERRIDES[`${pageId}¦${name}`];
}

/**
 * 批次 52：OPTION_SET_OVERRIDES 命中時，提供 base Select 元件（options 形＝
 * `{value,label}`）用的選項；未命中回 undefined（呼叫端 fallback 原常數）。
 * 供自畫 body（CourseStaticListBody／VoteMaintenanceBody／DeptManagerRecommendBody）
 * 接線用——override 命中＝凍結選項集取代寫死常數（placeholder 由呼叫端同步去除，
 * 凍結首項已是 prompt 等值選項）。
 */
export function selectOptionsOverride(
  pageId: string,
  name: string,
): readonly { value: string; label: string }[] | undefined {
  const override = OPTION_SET_OVERRIDES[`${pageId}¦${name}`];
  return override === undefined ? undefined : override.map((o) => ({ value: o.value, label: o.text }));
}

/** select 的可選項。static 用列舉代碼；dynamic 用雛形假資料（值不參與比對）。 */
export function optionsFor(
  pageId: string,
  field: LegacyFieldControl,
): readonly LegacyFieldOption[] {
  const cacheKey = `${pageId}¦${field.name}`;
  // 批次 49（規格 §4.4；Lead 裁定 ruling-1 核准本 hook 點）：OPTION_SET_OVERRIDES 命中＝
  // **整組取代** buildValues 假選項（replace 語意；不進 dynamicCache——override 是靜態常數）。
  // 空陣列＝legacy 初始態 0 options（含 prompt 都不畫，渲染端 flag 由呼叫端判斷）。
  // 未命中的頁零影響（下方 buildValues 假選項路徑完全不動）。
  // 批次 52（§8 裁決 D）：語意**延伸到 static select**——course 家族 kind 等列舉在
  // generated 抽到的是多數決變體，凍結頁面態不同（如 [全部('')]），generated 紅線不動，
  // 改由 override 表承接（replace 語意同上；role 列舉等 static 資料同理）。
  const override = OPTION_SET_OVERRIDES[cacheKey];
  if (override !== undefined) {
    return override;
  }
  if (field.optionsKind === 'static') {
    return field.options ?? [];
  }
  if (field.optionsKind !== 'dynamic') {
    return [];
  }
  const hit = dynamicCache.get(cacheKey);
  if (hit !== undefined) {
    return hit;
  }
  const label = field.label === '' ? field.name : field.label;
  const rule = matchKindRule(label);
  const kind = inferKind(label);
  const column: Column =
    rule?.pool === undefined
      ? { key: 'value', label, kind }
      : { key: 'value', label, kind, pool: rule.pool };
  const values: LegacyFieldOption[] = [];
  // W6（批次 42，issue #40）：非首項寫死選項（第二項起），插在 seed 之前。
  const extras = EXTRA_SELECT_OPTIONS[cacheKey];
  if (extras !== undefined) {
    values.push(...extras);
  }
  // 【W7（批次 42，issue #40，裁決②案 B）】GNL-002 的寫死首項常數**不再在此 prepend**——
  // 原本 optionsFor prepend＋渲染端（LegacyContractFields）又畫一次 prompt option＝雙重渲染
  // （recon-A #31/#37/#42/#50 的 dup）。案 B：首項由**渲染端承擔**（selectPromptFor），
  // 本函式只回 seed（＋W6 附加項）；無 override 的 select 完全不受影響（零回歸面）。
  // selectPromptFor 的來源註解（全部/請選擇/未設定/不包含次科室/共用表單＝結構性鏡像，
  // ePortfolio.js prepend/append；accountV2.jsp:73/152）見 dynamic-select-prompt.ts 檔頭。
  for (let index = 0; index < 3; index += 1) {
    const raw = buildValues(`${pageId}#${field.name}`, index, [column]).value;
    const text = raw === undefined || raw === null ? '' : String(raw);
    if (text !== '' && !values.some((v) => v.value === text)) {
      values.push({ value: text, text });
    }
  }
  dynamicCache.set(cacheKey, values);
  return values;
}

/**
 * 這個控制項有沒有「使用者可以改值」的事件？
 *
 * false 的兩類（見檔頭「不可驅動的控制項」）：
 *   ① hidden —— React 不對 `input[type=hidden]` 派發 change/input 的 onChange。
 *   ② select 但一個「非空值」的選項都選不到 —— 選了也送不出可辨識的值。
 */
export function isUserDrivable(pageId: string, field: LegacyFieldControl): boolean {
  if (field.hidden) return false;
  if (field.type === 'select' || field.type === 'select-multiple') {
    return optionsFor(pageId, field).some((option) => option.value !== '');
  }
  return true;
}

/**
 * 這個 hidden 欄位要帶進表單的初始值。
 * 只帶「與資料列無關的常數參數」，資料型的一律空字串（見檔頭的取捨說明）。
 * 非 hidden 欄位一律空字串——**不搬原系統的資料值**。
 */
export function seedValueFor(field: LegacyFieldControl): string {
  if (!field.hidden) return '';
  const raw = field.value ?? '';
  if (raw === '') return '';
  // 擷取當下算出來的日期：不是常數參數。
  if (DATE_LIKE.test(raw)) return '';
  // 指向真實資料列的識別碼（哨兵值除外）。
  if (FOREIGN_KEY_NAME.test(field.name) && !SENTINEL_VALUES.has(raw)) return '';
  return raw;
}

/** 這一頁表單的初始值。
 * pageId 提供時，static select 命中 SELECT_DEFAULT_OVERRIDES（W9 批次 42 裁決①B）
 * 的欄位初始值接 legacy selected；其餘欄位維持 seedValueFor 的原則。 */
export function initialValues(
  fields: readonly LegacyFieldControl[],
  pageId?: string,
): Record<string, string> {
  const values: Record<string, string> = {};
  for (const field of fields) {
    values[field.name] = seedValueFor(field);
    // W9：static select（非 multiple）命中白名單 → 初始值接 legacy selected。
    // 批次 52（§8 裁決 C，值面「含」~20 筆）：白名單延伸到 dynamic select——§4/§6 逐欄表
    // 列出的初始值（form-plan-form-list 5 鍵／co-edit query 4 鍵／class-query 3 鍵／
    // course-clazz-query-list 1 鍵）接凍結 selected；DOM 零差（selected 屬性 canonical 清、
    // select value 屬性不序列化），收斂面＝查詢送出值與 legacy 一致。
    if (pageId !== undefined && field.optionsKind !== null && !field.multiple) {
      const selected = selectDefaultFor(pageId, field.name);
      if (selected !== undefined) values[field.name] = selected;
    }
  }
  return values;
}

/**
 * 送進 DataSource 的條件／payload。
 *
 * 兩條規則：
 *   - **不可驅動**的欄位（hidden、選不到非空值的 select）：**一律帶**，即使值是空字串。
 *     它們沒有使用者事件可以改值，「接線」只可能是「值一定會被送出」（criteria C5-h）。
 *   - 可驅動的欄位：**空的就不帶**。若空值也照帶，就算控制項完全沒接 onChange，
 *     spy 一樣看得到那個 key，指標會恆為 100% 而失去鑑別力
 *     （C6 變異 2「拿掉 onChange」必須要能紅）。
 *
 * `fields` 省略時退回「空的就不帶」的舊行為（給沒有契約欄位的呼叫端用）。
 */
export function toPayload(
  values: Readonly<Record<string, string>>,
  fields?: readonly LegacyFieldControl[],
  pageId?: string,
): Record<string, string> {
  const seeds = new Map<string, string>();
  if (fields !== undefined && pageId !== undefined) {
    for (const field of fields) {
      if (!isUserDrivable(pageId, field)) seeds.set(field.name, seedValueFor(field));
    }
  }
  const out: Record<string, string> = {};
  for (const [name, value] of Object.entries(values)) {
    if (value !== '' || seeds.has(name)) out[name] = value;
  }
  // 不可驅動的欄位就算完全沒有進過 `values`（例如表單 state 還沒被初始化過），
  // 也必須帶出去——它們沒有使用者事件可以把自己放進 values。
  for (const [name, seed] of seeds) {
    if (!Object.hasOwn(out, name)) out[name] = seed;
  }
  return out;
}
